// utils/mailUtils.js
// Frontend helpers for sending email via your Firebase callable Cloud Function
// and attaching a CSV of selected vehicles.
// Usage examples at the bottom.

/* ---------------------------------------
 * Firebase callable: sendInternalEmail
 * -------------------------------------*/
import { getFunctions, httpsCallable } from "firebase/functions";

/**
 * Call the Firebase callable "sendInternalEmail" Cloud Function.
 * Expects your backend to use Microsoft Graph app-only auth and accept:
 * { toRecipients, ccRecipients?, bccRecipients?, subject, htmlBody, attachments?, replyTo? }
 */
export async function sendInternalEmail({
  toRecipients,
  subject,
  htmlBody,
  ccRecipients,
  bccRecipients,
  replyTo,
  attachments,
}) {
  const functions = getFunctions();
  const fn = httpsCallable(functions, "sendInternalEmail");
  const { data } = await fn({
    toRecipients,
    ccRecipients,
    bccRecipients,
    replyTo,
    subject,
    htmlBody,
    attachments, // [{ name, contentType, contentBytes(base64) }]
  });
  return data; // { ok: true }
}

/* ---------------------------------------
 * CSV helpers (VIN, Year, Make, Model, Color)
 * -------------------------------------*/

/** Escape a single CSV cell */
function escapeCsv(val) {
  if (val === null || val === undefined) return "";
  const s = String(val);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** If sometimes Firestore DocumentSnapshots are passed, unwrap them */
function toPlain(v) {
  return typeof v?.data === "function" ? { id: v.id, ...v.data() } : v;
}

/** De-duplicate rows by VIN (keep the first occurrence) */
function uniqueByVin(arr) {
  const seen = new Set();
  return arr.filter((v) => {
    const vin = v?.strVin;
    if (!vin || seen.has(vin)) return false;
    seen.add(vin);
    return true;
  });
}

/** Optional: convert "GBA - Black" → "Black" */
function cleanColor(color) {
  if (!color) return "";
  return String(color).replace(/^[^-\n]*-\s*/, "").trim();
}

/** UTF-8 safe base64 encoder (works in browsers) */
function base64EncodeUtf8(str) {
  if (typeof TextEncoder !== "undefined") {
    const bytes = new TextEncoder().encode(str);
    let binary = "";
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    return btoa(binary);
  }
  // Fallback (older browsers)
  // eslint-disable-next-line no-undef
  return btoa(unescape(encodeURIComponent(str)));
}

/**
 * Build a CSV string with exactly: VIN, Year, Make, Model, Color
 * @param {Array<Object>} vehicles - array of plain objects or Firestore snapshots
 * @param {Object} [options]
 * @param {boolean} [options.stripColorPrefix=true] - remove code prefix like "GBA - "
 */
export function vehiclesToCsvBasic(vehicles, { stripColorPrefix = true } = {}) {
  const header = ["VIN", "Year", "Make", "Model", "Color"];
  const plain = uniqueByVin((vehicles || []).map(toPlain));

  const rows = plain.map((v) => {
    const color = stripColorPrefix ? cleanColor(v?.strColor) : (v?.strColor ?? "");
    return [v?.strVin || "", v?.strYR ?? "", v?.strMake ?? "", v?.strModel ?? "", color];
  });

  const lines = [header, ...rows].map((r) => r.map(escapeCsv).join(","));
  return lines.join("\n");
}

/**
 * Build a Microsoft Graph–compatible CSV attachment from vehicles.
 * @returns {Array<Object>} attachments array suitable for Graph sendMail
 */
export function makeCsvAttachmentFromVehicles(
  vehicles,
  filename = "vehicles.csv",
  options
) {
  const csv = vehiclesToCsvBasic(vehicles, options);
  return [
    {
      name: filename,
      contentType: "text/csv",
      contentBytes: base64EncodeUtf8(csv), // base64 string
    },
  ];
}

/* ---------------------------------------
 * Convenience: build CSV + send in one call
 * -------------------------------------*/

/**
 * Convenience helper that creates the CSV attachment and sends the email.
 * @param {Object} params
 * @param {Array<Object>} params.vehicles
 * @param {string[]} params.toRecipients
 * @param {string} params.subject
 * @param {string} params.htmlBody
 * @param {string[]} [params.ccRecipients]
 * @param {string[]} [params.bccRecipients]
 * @param {string} [params.replyTo]
 * @param {string} [params.filename] - optional CSV filename
 * @param {boolean} [params.stripColorPrefix=true] - clean colors like "GBA - Black"
 */
export async function sendVehiclesEmail({
  vehicles,
  toRecipients,
  subject,
  htmlBody,
  ccRecipients,
  bccRecipients,
  replyTo,
  filename,
  stripColorPrefix = true,
}) {
  const name =
    filename || `vehicles_${new Date().toISOString().slice(0, 10)}.csv`;

  const attachments = makeCsvAttachmentFromVehicles(vehicles, name, {
    stripColorPrefix,
  });

  return sendInternalEmail({
    toRecipients,
    ccRecipients,
    bccRecipients,
    replyTo,
    subject,
    htmlBody,
    attachments,
  });
}

